/**
 * The one map (master prompt 2.4). Every route, back-office menu item, admin tab, designer and form
 * field has `op`: keep | collapse | consolidate | vault, and an optional `feature` flag.
 * Simple hides collapse + vault. A false flag hides its rows in both modes.
 */
export const FEATURE_KEYS = [
    "mall", "directory", "marketplace", "billboards", "spaces", "community", "listings", "service_requests",
    "classes", "membership_tiers", "alt_payments", "homepage_slots", "same_day_buckets", "legacy_extras",
];

export const ROUTES = {
    "/": { op: "consolidate", title: "Order" },
    "/order/:slug": { op: "consolidate", title: "Order" },
    "/print": { op: "keep", title: "Flyers & Print" }, "/print/:slug": { op: "keep", title: "Flyers & Print" },
    "/order/white-tees": { op: "consolidate" }, "/order/colored-apparel": { op: "consolidate" },
    "/order/dtf-gang-sheet": { op: "consolidate" }, "/order/custom-sign": { op: "consolidate" },
    "/shirts": { op: "consolidate", title: "Shirts" },
    "/shirts/:kind": { op: "keep", title: "Shirts" },
    "/shirts/:kind/:slug": { op: "consolidate", title: "Shirts" },
    "/shirts/dtf": { op: "consolidate", title: "DTF Transfers" },
    "/shirts/dtf/gang-sheet": { op: "consolidate", title: "Gang Sheet" },
    "/signs": { op: "consolidate", title: "Signs" },
    "/signs/:category": { op: "consolidate", title: "Signs" },
    "/signs/:category/:slug": { op: "consolidate", title: "Signs" },
    "/artwork-request": { op: "consolidate", title: "Artwork" },
    "/cart": { op: "keep", title: "Cart" },
    "/checkout": { op: "consolidate", title: "Checkout" },
    "/checkout/:store": { op: "consolidate", title: "Checkout" },
    "/payment/success": { op: "consolidate", title: "Order confirmed" },
    "/payment/cancel": { op: "consolidate", title: "Cart" },
    "/order-confirmation/:id": { op: "consolidate", title: "Order confirmed" },
    "/track": { op: "keep", title: "Track your order" },
    "/track/:code": { op: "keep", title: "Track your order" },
    "/pay": { op: "keep", title: "Pay a balance" },
    "/artwork/pay": { op: "keep", title: "Pay for artwork" },
    "/r/:token": { op: "keep", title: "Reorder" },
    "/proof/:token": { op: "keep", title: "Approve your proof" },
    "/login": { op: "keep", title: "Log in" },
    "/register": { op: "keep", title: "Create an account" },
    "/account": { op: "consolidate", title: "My orders" },
    "/account/purchases": { op: "consolidate", title: "My orders" },
    "/stores": { op: "vault", feature: "directory" },
    "/stores/:slug": { op: "keep", title: "Order" },
    "/stores/:slug/policies": { op: "keep", title: "Policies" },
    "/site/:slug": { op: "keep" }, "/site/exit": { op: "keep" },
    "/whitelabel": { op: "keep", title: "Start your own print shop" },
    "/signup/shop": { op: "keep", title: "Start your shop" },
    "/wholesale": { op: "keep", title: "Wholesale" },
    "/services": { op: "vault", feature: "legacy_extras" },
    "/website-design": { op: "keep", title: "Websites" },
    "/shop/:bucket": { op: "vault", feature: "same_day_buckets" },
    "/directory": { op: "vault", feature: "directory" },
    "/mall": { op: "vault", feature: "mall" }, "/mall/setup": { op: "vault", feature: "mall" }, "/mall/:slug": { op: "vault", feature: "mall" },
    "/supplies": { op: "keep", title: "Classifieds" }, "/supplies/sell": { op: "keep", title: "Post an ad" },
    "/supplies/:slug": { op: "keep", title: "Classifieds" },
    "/spaces": { op: "vault", feature: "spaces" },
    "/community": { op: "vault", feature: "community" }, "/food-court": { op: "vault", feature: "community" },
    "/classes": { op: "keep", title: "Classes" },
    // shop owner
    "/account/orders": { op: "consolidate" }, "/account/orders/:id/ticket": { op: "keep" },
    "/account/products": { op: "consolidate" }, "/account/store": { op: "consolidate" },
    "/account/domain": { op: "consolidate" }, "/account/wizard": { op: "vault", feature: "legacy_extras" },
    "/account/consignment": { op: "vault", feature: "legacy_extras" }, "/account/payouts": { op: "consolidate" },
    "/account/payout-history": { op: "consolidate" }, "/account/analytics": { op: "collapse" },
    "/account/coupons": { op: "collapse" }, "/account/discounts": { op: "collapse" },
    "/account/gang-sheets": { op: "collapse" }, "/account/inbox": { op: "collapse" },
    "/account/email-guide": { op: "collapse" }, "/account/drop-cards": { op: "vault", feature: "legacy_extras" },
    "/account/reviews": { op: "collapse" }, "/account/homepage": { op: "vault", feature: "homepage_slots" },
    "/account/billboards": { op: "vault", feature: "billboards" },
    "/account/listings": { op: "keep" }, "/account/sell": { op: "vault", feature: "listings" },
    "/account/service-requests": { op: "vault", feature: "service_requests" },
    "/account/classes": { op: "vault", feature: "classes" },
    "/admin": { op: "consolidate", title: "Platform admin" },
};

/** Back-office menu rows that are not routes of their own (hash links, outside pages). */
export const NAV_EXTRA = {
    "/account/store#counters": { op: "consolidate" }, "/account/store#billboard": { op: "vault", feature: "billboards" },
    "/account/store#ops": { op: "consolidate" }, "/account/store#socials": { op: "collapse" },
};

export const ADMIN_TABS = {
    orders: { op: "consolidate" }, artwork: { op: "consolidate" }, overview: { op: "collapse" },
    shops: { op: "consolidate" }, payouts: { op: "consolidate" }, customers: { op: "collapse" },
    leads: { op: "collapse" }, notifications: { op: "collapse" }, email: { op: "collapse" },
    coupons: { op: "collapse" }, calls: { op: "collapse" }, livechat: { op: "collapse" },
    classes: { op: "vault", feature: "classes" }, marketplace: { op: "vault", feature: "marketplace" },
    billboards: { op: "vault", feature: "billboards" },
};

export const DESIGNERS = {
    shirt: { op: "keep" }, orderStudio: { op: "consolidate" }, gangSheet: { op: "keep" }, dtfPieces: { op: "keep" },
    sign: { op: "consolidate" }, dropCard: { op: "vault", feature: "legacy_extras" }, billboard: { op: "vault", feature: "billboards" },
};

export const FIELDS = {
    design: { op: "keep" }, size: { op: "keep" }, quantity: { op: "keep" }, color: { op: "keep" }, sides: { op: "keep" },
    note: { op: "consolidate" }, need_by: { op: "keep" }, print_ready: { op: "consolidate" }, customer_supplied: { op: "keep" },
    handoff: { op: "keep" }, contact: { op: "consolidate" }, terms: { op: "consolidate" },
    garment_style: { op: "collapse" }, placement: { op: "collapse" }, lead_time: { op: "collapse" },
    sign_variants: { op: "collapse" }, sign_finishing: { op: "consolidate" }, card_stock: { op: "consolidate" },
    dtf_film: { op: "collapse" }, print_method: { op: "collapse" }, coupon: { op: "collapse" }, bulk_table: { op: "collapse" },
    artwork_charges: { op: "collapse" }, alt_payments: { op: "vault", feature: "alt_payments" },
    dtf_tiers: { op: "vault", feature: "membership_tiers" },
};

/** Is this row shown? Collapse/vault vanish in Simple; a false flag hides it in both modes. */
export const shown = (rule, { isSimple, features }) => {
    if (!rule) return true;
    if (rule.feature && !features?.[rule.feature]) return false;
    if (isSimple && (rule.op === "collapse" || rule.op === "vault")) return false;
    return true;
};

const toPattern = (p) => new RegExp("^" + p.replace(/\//g, "\\/").replace(/:[^/]+/g, "[^/]+") + "$");
const PATTERNS = Object.entries(ROUTES).map(([p, rule]) => [toPattern(p), rule]);

/** The rule for a concrete pathname (`/signs/banner` → the `/signs/:category` row). */
export const ruleFor = (pathname) => {
    const path = pathname.replace(/\/$/, "") || "/";
    if (ROUTES[path]) return ROUTES[path];
    const hit = PATTERNS.find(([re]) => re.test(path));
    return hit ? hit[1] : null;
};
